// Every chart in the web app, with its own defaults - what it looks like
// until an Admin changes it on Administration -> Appearance -> Charts.
// Each has sample data, so the settings page can preview it.
import type { AxisSettings, LabelPosition, SeriesType } from './chartSettings';

export type ValueFormat = 'number' | 'litres' | 'currency' | 'percent' | 'cows' | 'kg' | 'rate';

export interface SeriesDef {
  key: string; // data key - nested paths like "morning.litres" work
  name: string;
  type: SeriesType;
  color: string;
  axis?: 'left' | 'right';
  stack?: string; // bars in the same stack sit on top of each other
  format?: ValueFormat;
  width?: number;
  dash?: 'solid' | 'dashed' | 'dotted' | 'dashdot';
  labels?: boolean; // data labels on by default (default true)
  labelPosition?: LabelPosition;
  labelDy?: number; // how far above (-) / below (+) a line's point its label sits
  labelColor?: string;
  optional?: boolean; // left out when it has no data (e.g. a third milking)
  alt?: { label: string; when: (row: any) => boolean; color: string }; // per-point second colour
}

export interface ChartDef {
  id: string;
  name: string; // in the picker
  page: string; // where it appears
  title: string;
  showTitle: boolean;
  xKey: string;
  xFormat: 'date' | 'text';
  series: SeriesDef[];
  height: number;
  left: AxisSettings & { width?: number; integer?: boolean };
  right?: AxisSettings & { width?: number; integer?: boolean; headroom?: number; minMax?: number };
  majorEvery?: number;
  xAngle?: number;
  xAllLabels?: boolean;
  legend: boolean;
  gridV: boolean;
  gridDashed: boolean;
  labelMode: 'auto' | 'all' | 'last' | 'none';
  showTotal?: boolean; // the whole stack's total above it
  barRadius: number;
  maxBarSize?: number;
  /** Extra tooltip text beside a value (e.g. that shift's Fat %). */
  tooltipExtra?: (row: any, key: string) => string | undefined;
  sample: () => any[];
}

// ---------- sample data for the previews ----------
function days(n: number): string[] {
  const out: string[] = [];
  const d = new Date();
  d.setDate(d.getDate() - n + 1);
  for (let i = 0; i < n; i++) {
    out.push(d.toISOString().slice(0, 10));
    d.setDate(d.getDate() + 1);
  }
  return out;
}
const wave = (i: number, base: number, amp: number) => Math.round((base + amp * Math.sin(i / 1.7) + (i % 3) * amp * 0.15) * 10) / 10;
const months = ['2026-03', '2026-04', '2026-05', '2026-06', '2026-07', '2026-08', '2026-09', '2026-10'];

function milkDays() {
  let cum = 0;
  return days(14).map((date, i) => {
    const m = wave(i, 30, 4);
    const e = wave(i + 2, 24, 3);
    cum += (m + e) * 48;
    return {
      production_date: date, morning_litres: m, evening_litres: e, third_litres: 0, total_litres: Math.round((m + e) * 10) / 10,
      avg_rate_per_litre: 46 + (i % 4), morning: { litres: m, fat_percent: wave(i, 4.4, 0.3) }, evening: { litres: e, fat_percent: wave(i + 1, 4.9, 0.3) },
      third: { litres: 0 }, cumulative_amount: Math.round(cum), morning_fat_percent: wave(i, 4.4, 0.3), evening_fat_percent: wave(i + 1, 4.9, 0.3),
      day_fat_percent: wave(i + 0.5, 4.6, 0.25),
    };
  });
}

const FAT_LEFT: ChartDef['left'] = { min: 3.2, max: 6, step: 0.1, labelEvery: 2, decimals: 1, suffix: '%', width: 42 };
const fatSeries = (morning: string, evening: string, day?: string): SeriesDef[] => [
  { key: morning, name: 'Morning Fat %', type: 'line', color: '#1b5c8f', format: 'percent', labelDy: -10 },
  { key: evening, name: 'Evening Fat %', type: 'line', color: '#b26e12', format: 'percent', labelDy: 16 },
  ...(day ? [{ key: day, name: 'Day Fat %', type: 'line' as const, color: '#2f6335', format: 'percent' as const, width: 2.5, dash: 'dashed' as const, labelDy: -22, optional: true }] : []),
];
const fatDef = (id: string, name: string, page: string, title: string, xKey: string, x: 'date' | 'text', s: SeriesDef[], sample: () => any[]): ChartDef => ({
  id, name, page, title, showTitle: true, xKey, xFormat: x, series: s, height: 320, left: FAT_LEFT, majorEvery: 0.5,
  legend: true, gridV: false, gridDashed: false, labelMode: 'auto', barRadius: 0, sample,
});

export const CHARTS: ChartDef[] = [
  {
    id: 'dashboard-milk', name: 'Milk Production (litres, rate)', page: 'Dashboard', title: 'Milk Production', showTitle: false,
    xKey: 'production_date', xFormat: 'date', height: 300, legend: true, gridV: true, gridDashed: true, labelMode: 'all', showTotal: true, barRadius: 3,
    left: { width: 40 }, right: { width: 40 },
    series: [
      { key: 'morning_litres', name: 'Morning', type: 'bar', color: '#2f6335', stack: 'litres', format: 'litres', labels: false },
      { key: 'evening_litres', name: 'Evening', type: 'bar', color: '#8fbd91', stack: 'litres', format: 'litres', labels: false },
      { key: 'third_litres', name: 'Third', type: 'bar', color: '#639e67', stack: 'litres', format: 'litres', labels: false, optional: true },
      { key: 'avg_rate_per_litre', name: 'Rate (₹/L)', type: 'line', color: '#1b5c8f', axis: 'right', format: 'rate', labelDy: 16 },
    ],
    sample: milkDays,
  },
  fatDef('dashboard-fat', 'Fat % — Morning vs Evening', 'Dashboard', 'Fat % — Morning vs Evening', 'production_date', 'date',
    fatSeries('morning_fat_percent', 'evening_fat_percent'), milkDays),
  {
    id: 'reports-milk', name: 'Daily Milk Production (litres, cumulative ₹)', page: 'Reports', title: 'Daily Milk Production', showTitle: false,
    xKey: 'production_date', xFormat: 'date', height: 320, legend: true, gridV: true, gridDashed: true, labelMode: 'all', showTotal: true, barRadius: 3,
    left: { title: 'Litres', width: 40 }, right: { title: 'Cumulative ₹', width: 60 },
    series: [
      { key: 'morning.litres', name: 'Morning', type: 'bar', color: '#2f6335', stack: 'litres', format: 'litres', labels: false },
      { key: 'evening.litres', name: 'Evening', type: 'bar', color: '#8fbd91', stack: 'litres', format: 'litres', labels: false },
      { key: 'third.litres', name: 'Third', type: 'bar', color: '#639e67', stack: 'litres', format: 'litres', labels: false, optional: true },
      { key: 'cumulative_amount', name: 'Cumulative Amount', type: 'line', color: '#1b5c8f', axis: 'right', format: 'currency', labelDy: -14 },
    ],
    tooltipExtra: (row, key) => {
      const shift = row?.[key.split('.')[0]];
      return key.endsWith('.litres') && shift?.fat_percent != null ? `Fat ${Number(shift.fat_percent).toFixed(2)}%` : undefined;
    },
    sample: milkDays,
  },
  fatDef('reports-fat', 'Fat % — Morning, Evening and the Day', 'Reports', 'Fat % — Morning, Evening and the Day', 'production_date', 'date',
    fatSeries('morning.fat_percent', 'evening.fat_percent', 'day_fat_percent'), milkDays),
  {
    id: 'reports-sales-litres', name: 'Milk Sales — Litres by buyer group', page: 'Reports', title: 'Milk (Litres)', showTitle: true,
    xKey: 'label', xFormat: 'text', height: 280, legend: true, gridV: false, gridDashed: false, labelMode: 'all', barRadius: 4, maxBarSize: 56,
    xAngle: -20, xAllLabels: true, left: { suffix: ' L', width: 40 },
    series: [{
      key: 'total_litres', name: 'Sold', type: 'bar', color: '#1b5c8f', format: 'litres', labelColor: '#33392f',
      alt: { label: 'Consumed (₹0)', when: (row) => !!row?.consumption, color: '#b26e12' },
    }],
    tooltipExtra: (row) => (row?.total_amount != null ? `₹${Math.round(Number(row.total_amount)).toLocaleString('en-IN')}` : undefined),
    sample: () => [
      { label: 'Partner', total_litres: 412.5, total_amount: 19800 },
      { label: 'Hub', total_litres: 860, total_amount: 38700 },
      { label: 'Customer', total_litres: 236, total_amount: 14160 },
      { label: 'Calves', total_litres: 64, total_amount: 0, consumption: true },
      { label: 'Office', total_litres: 18, total_amount: 0, consumption: true },
    ],
  },
  {
    id: 'reports-sales-amount', name: 'Milk Sales — Amount by buyer group', page: 'Reports', title: 'Amount (₹)', showTitle: true,
    xKey: 'label', xFormat: 'text', height: 280, legend: false, gridV: false, gridDashed: false, labelMode: 'all', barRadius: 4, maxBarSize: 56,
    xAngle: -20, xAllLabels: true, left: { width: 48 },
    series: [{ key: 'total_amount', name: 'Amount (₹)', type: 'bar', color: '#3f7d44', format: 'currency', labelColor: '#33392f' }],
    sample: () => [
      { label: 'Partner', total_amount: 19800 }, { label: 'Hub', total_amount: 38700 }, { label: 'Customer', total_amount: 14160 },
    ],
  },
  {
    id: 'register-herd-milk', name: 'Herd milk per day, AM / PM — and cows milked', page: 'Cattle Register → Dashboard',
    title: 'Herd milk per day, AM and PM (L) — and cows milked', showTitle: true,
    xKey: 'date', xFormat: 'date', height: 260, legend: true, gridV: false, gridDashed: false, labelMode: 'all', showTotal: true, barRadius: 4,
    left: { width: 40 }, right: { width: 32, integer: true, headroom: 2, minMax: 5 },
    series: [
      { key: 'am', name: 'AM', type: 'bar', color: '#2f6335', stack: 'milk', format: 'litres', labelPosition: 'center' },
      { key: 'pm', name: 'PM', type: 'bar', color: '#8fbd91', stack: 'milk', format: 'litres', labelPosition: 'center' },
      { key: 'animals', name: 'Cows milked', type: 'line', color: '#b26e12', axis: 'right', format: 'cows', labelPosition: 'top' },
    ],
    sample: () => days(10).map((date, i) => {
      const am = wave(i, 30, 4);
      const pm = wave(i + 2, 24, 3);
      return { date, am, pm, total: Math.round((am + pm) * 10) / 10, animals: 8 + (i % 3) };
    }),
  },
  {
    id: 'animal-milk', name: 'Animal — Lifetime milk by month', page: 'Animal Lifecycle', title: 'Lifetime Milk Production', showTitle: false,
    xKey: 'month', xFormat: 'text', height: 260, legend: false, gridV: true, gridDashed: true, labelMode: 'all', barRadius: 3, left: { width: 40 },
    series: [{ key: 'total_litres', name: 'Litres', type: 'bar', color: '#2f6335', format: 'litres', labelColor: '#33392f' }],
    sample: () => months.map((month, i) => ({ month, total_litres: wave(i, 320, 60) })),
  },
  fatDef('animal-fat', 'Animal — Fat % by month', 'Animal Lifecycle', 'Fat % — Morning vs Evening', 'month', 'text',
    fatSeries('morning_fat_percent', 'evening_fat_percent'),
    () => months.map((month, i) => ({ month, morning_fat_percent: wave(i, 4.3, 0.3), evening_fat_percent: wave(i + 1, 4.8, 0.3) }))),
  {
    id: 'animal-growth', name: 'Animal — Weight over time', page: 'Animal Lifecycle', title: 'Growth', showTitle: false,
    xKey: 'record_date', xFormat: 'date', height: 260, legend: false, gridV: true, gridDashed: true, labelMode: 'all', barRadius: 3,
    left: { title: 'kg', width: 40 },
    series: [{ key: 'weight_kg', name: 'Weight', type: 'line', color: '#1b5c8f', format: 'kg', labelPosition: 'top' }],
    sample: () => days(8).map((record_date, i) => ({ record_date, weight_kg: 180 + i * 14 + (i % 2) * 3 })),
  },
  {
    id: 'animal-expenditure', name: 'Animal — Expenditure by month', page: 'Animal Lifecycle', title: 'Lifetime Expenditure', showTitle: false,
    xKey: 'month', xFormat: 'text', height: 220, legend: false, gridV: true, gridDashed: true, labelMode: 'all', barRadius: 3, left: { width: 50 },
    series: [{ key: 'amount', name: 'Spent', type: 'bar', color: '#8e5013', format: 'currency', labelColor: '#33392f' }],
    sample: () => months.slice(2).map((month, i) => ({ month, amount: [42000, 1200, 850, 3100, 600, 1500][i] })),
  },
];

export function chartDef(id: string): ChartDef {
  const def = CHARTS.find((c) => c.id === id);
  if (!def) throw new Error(`Unknown chart ${id}`);
  return def;
}

// Quick colour sets for every series at once (Excel's "Change Colors").
export const PALETTES: { id: string; label: string; colors: string[] }[] = [
  { id: 'farm', label: 'Farm (default)', colors: ['#2f6335', '#8fbd91', '#639e67', '#1b5c8f', '#b26e12'] },
  { id: 'office', label: 'Office', colors: ['#4472c4', '#ed7d31', '#a5a5a5', '#ffc000', '#5b9bd5', '#70ad47'] },
  { id: 'bright', label: 'Colourful', colors: ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b'] },
  { id: 'safe', label: 'Colour-blind safe', colors: ['#0072b2', '#e69f00', '#009e73', '#cc79a7', '#56b4e9', '#d55e00'] },
  { id: 'green', label: 'Shades of green', colors: ['#1b361f', '#3f7d44', '#8fbd91', '#26502b', '#639e67'] },
  { id: 'blue', label: 'Shades of blue', colors: ['#164a73', '#2371b0', '#7fb2dd', '#1b5c8f', '#4f93cc'] },
  { id: 'warm', label: 'Warm', colors: ['#b32f21', '#d38f18', '#e7a92a', '#8e5013', '#f4d98d'] },
  { id: 'grey', label: 'Greyscale', colors: ['#33392f', '#7c8276', '#a2a89b', '#5b6156', '#c9ccc4'] },
];
